import unset from 'lodash/unset';
import { t } from 'i18next';
import _ from 'lodash';

// IN-VIEW that omit will be removed from lodash
export const omit = (originalObject = {}, keysToOmit = []) => {
  const clonedObject = { ...originalObject };

  keysToOmit.forEach(path => unset(clonedObject, path));
  return clonedObject;
};

export const formatMessage = ({ message, value = '' }) => {
  const placeholder = '{value}';
  const m = t(message);
  const v = t(value);
  return m.split(placeholder).join(v);
};

export const capitalizeFirstLetter = value => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const convertSnakeCaseToText = text => {
  if (!text) {
    return text;
  }

  const textArray = text
    .split('_')
    .map(word => capitalizeFirstLetter(word.toLowerCase()));

  return textArray.join(' ');
};

export const checkValueForString = value => {
  return typeof value === 'string';
};

export const getFormInitialValues = data => {
  const displayedValues = {
    ...data.generalInfo,
    ...data.detailedInfo,
    ...data.others,
  };
  const initial = {};
  Object.entries(displayedValues).forEach(key => {
    initial[key[1].field || key[0]] = key[1].idValue
      ? key[1].idValue
      : checkValueForString(key[1].value)
      ? key[1].value === '-'
        ? undefined
        : key[1].value
      : key[1].value;
  });
  return initial;
};

export const getTextFromOptions = (status, options, labelKey = 'name') => {
  if (!status) return '-';

  const foundInOptions = options.find(item => item.id === status);

  return foundInOptions
    ? foundInOptions[labelKey]
    : convertSnakeCaseToText(status);
};

export const filterOptions = ({ options, filterKey, filterValue }) => {
  const filteredItems = options.filter(item => item[filterKey] === filterValue);
  return filteredItems.length < 1
    ? filterValue
      ? []
      : options
    : filteredItems;
};

export const deepSearch = ({ object, key, condition }) => {
  if (!object) return null;

  if (
    Object.prototype.hasOwnProperty.call(object, key) &&
    condition(key, object) === true
  )
    return object;

  for (let i = 0; i < Object.keys(object).length; i += 1) {
    const value = object[Object.keys(object)[i]];
    if (typeof value === 'object' && value != null) {
      const o = deepSearch({
        object: object[Object.keys(object)[i]],
        key,
        condition,
      });
      if (o != null) return o;
    }
  }
  return null;
};

export const isValidUrl = str => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return pattern.test(str);
};

export const getFieldValue = ({ item, props, defaultValue }) => {
  return item.isNested
    ? item.field.split('.').reduce((p, prop) => {
        return p ? p[prop] || defaultValue : defaultValue;
      }, props.values)
    : props.values[item.field] || defaultValue;
};

export const getErrorValue = ({ item, props }) => {
  return item.isNested
    ? item.field.split('.').reduce((p, prop) => {
        return p ? p[prop] || undefined : undefined;
      }, props.errors)
    : props.errors[item.field];
};

export const getNestedValue = ({ item, data }) => {
  return item.isNested
    ? item.field.split('.').reduce((p, prop) => {
        return p ? p[prop] || undefined : undefined;
      }, data)
    : data[item.field];
};

export const convertArrayStrToObject = ({ array = [] }) =>
  array.reduce((acc, c) => ({ ...acc, [c]: c }), {});

export const optionalFormField = ({
  showWhen,
  field = [],
  fieldFalse = [],
}) => {
  return showWhen
    ? Array.isArray(field)
      ? field
      : [field]
    : Array.isArray(fieldFalse)
    ? fieldFalse
    : [fieldFalse];
};
export const translateValue = value => {
  return t(value);
};
/**
 * This function extracts the word after a / character and translates it.  e.g. USD 100 / Month --> translates Month,
 * if the term does not contain a / then it just returns the orignal value
 * @param {string} value the string to be translated
 * @returns {string} value or value with the term after the / translated
 */
export const translateStringAfterSlash = value => {
  if (value && value.includes('/')) {
    const stringParts = value.split('/');
    if (stringParts.length === 2) {
      const suffix = translateValue(stringParts[1].trim());
      return `${stringParts[0].trim()} / ${suffix}`;
    }
  }
  return value;
};
/**
 * This function helps to convert dynamic strings to preferred language like ---> Update MY_NAME.
 * It only translates the key and not the value.
 * In language json file, it is stored as ---->  "update_value_title": "VN_UPDATE {{value}}",
 * where update_value_title is the key and {value:'MY_NAME'} is the object
 * @param {string} key json key
 * @param {object} value object that contains value for the key
 * @returns {string} translated string
 */
export const translatedKeyValueText = (key, { value }) =>
  t(key, {
    value,
  });
export const translateTwoValueSentence = (key, { value1, value2 }) =>
  t(key, {
    value1,
    value2,
  });

export const translatedPerformTaskStatus = (key, { taskstatus, taskname }) =>
  t(key, {
    taskstatus,
    taskname,
  });

export const convertText = text => t(text);

export const languageConverter = ({ details, arrayOfKeys }) => {
  if (!details) {
    return {};
  }
  const convertedDetails = _.cloneDeep(details);
  if (Object.keys(convertedDetails).length !== 0 && arrayOfKeys.length > 0) {
    const { detailedInfo, generalInfo, others } = convertedDetails;
    for (let i = 0; i < arrayOfKeys.length; i += 1) {
      if (detailedInfo[arrayOfKeys[i]]) {
        detailedInfo[arrayOfKeys[i]].type = 'textTranslated';
      } else if (generalInfo[arrayOfKeys[i]]) {
        generalInfo[arrayOfKeys[i]].type = 'textTranslated';
      } else if (others[arrayOfKeys[i]]) {
        others[arrayOfKeys[i]].type = 'textTranslated';
      }
    }
    return { detailedInfo, generalInfo, others };
  }
  return {};
};

export const getBrowserLocales = ({ languageCodeOnly = false }) => {
  const browserLocales = _.isEmpty(navigator.languages)
    ? [navigator.language]
    : navigator.languages;

  if (!browserLocales) {
    return undefined;
  }

  const requiredLangs = browserLocales.map(locale => {
    const trimmedLocale = locale.trim();

    return languageCodeOnly ? trimmedLocale.split(/-|_/)[0] : trimmedLocale;
  });
  return !_.isEmpty(requiredLangs) ? _.uniq(requiredLangs) : 'en';
};
